import { evaluateReadyGate } from './ready-gate';
import type { VariantSetDocument } from './types';

import type { QualityChipCheck, QualityChipVerdict } from '@/components/ui/inspector/QualityChip';

/**
 * Renders the Ready gate through the SAME chip the organic post editor uses.
 *
 * The gate itself is unchanged and stays the authority — `evaluateReadyGate`
 * still decides `ok`, and the store still refuses `Ready` past it. This only
 * gives the verdict a shape `QualityChip` can draw.
 *
 * ── WHY ───────────────────────────────────────────────────────────────────
 * Mora had two "is this ready to ship" gates with nothing in common. The post
 * editor's is a visible outline pill reading `1 to fix` that opens a list of
 * every check with a sentence each. The workbench's was a DISABLED MENU ROW in
 * `⋯ → Actions`, whose label was the blocker tooltip — so it sat directly
 * under an enabled `Save concept` row and read, at a glance, as one sentence:
 * _"Save Concept 4:5 still needs review."_ Founder, 2026-08-21: _"There's no
 * validation, and it's different… it does have a validator, but it looks a lot
 * different on the design page."_
 *
 * ── ONE CHECK PER SIZE, NOT ONE PER BLOCKER ───────────────────────────────
 * `ReadyGateVerdict.blockers` lists only what FAILS. Rendering that directly
 * would reproduce the exact defect `QualityChip` was built to fix: its own
 * header records that a readiness line which appears only when something is
 * wrong leaves a person unable to tell "everything was checked and passed"
 * from "nothing ran". So every variant in the set gets a row — a passing size
 * says so, by name.
 *
 * ── STATUS MAPPING ────────────────────────────────────────────────────────
 * `generating` maps to `skipped`, not `fail`. The check has not run yet and
 * there is nothing for the marketer to act on; a dashed circle says "pending"
 * where a red octagon would say "you broke this". Everything the gate actually
 * refuses on — no sizes, no image, unreviewed after a change — is a `fail`,
 * because `failCount` is what drives the chip's `N to fix` label and those are
 * precisely the N things to fix.
 */
export const READY_GATE_CHIP_COPY = {
  title: 'Ready checks',
  description:
    'Every size has to carry its own finished image and be reviewed after the last change before this concept can be marked Ready. Auto-resize is not automatic.',
  passLabel: (count: number) => (count === 1 ? '1 size ready' : `${count} sizes ready`),
  passTone: 'success',
  footer: 'Checked just now.',
} as const;

/**
 * Sizes whose card was made by an older renderer than a sibling's (`sizesFromOlderRenderer`). Such
 * a size still passes the gate, but "3 sizes ready" would be a claim about sizes that do not match
 * each other, so each is raised as a warning and the chip reads "N to review" instead.
 */
export interface ReadyGateRendererNote {
  olderRendererVariantIds: ReadonlySet<string>;
}

export function readyGateChipVerdict(
  doc: VariantSetDocument | null,
  renderer?: ReadyGateRendererNote,
): QualityChipVerdict {
  const gate = evaluateReadyGate(doc);
  const blockerByVariant = new Map(gate.blockers.map((b) => [b.variantId, b]));

  // The empty case is its own row rather than zero rows: "all zero variants
  // pass" is the vacuous truth `ready-gate.ts` exists to refuse, and an empty
  // popover would state it silently.
  if (!doc || doc.variants.length === 0) {
    const check: QualityChipCheck = {
      id: 'no-variants',
      label: 'Sizes',
      status: 'fail',
      detail: gate.blockers[0]?.message ?? 'This concept has no sizes yet.',
    };
    return { checks: [check], passCount: 0, warnCount: 0, failCount: 1 };
  }

  const checks: QualityChipCheck[] = [...doc.variants]
    .sort((a, b) => a.position - b.position)
    .map((variant) => {
      const blocker = blockerByVariant.get(variant.id);
      if (!blocker && renderer?.olderRendererVariantIds.has(variant.id)) {
        return {
          id: variant.id,
          label: variant.aspectRatio,
          status: 'warn' as const,
          detail: `${variant.aspectRatio} was made by an older card renderer than the other sizes, so it can look different. Use Regenerate all sizes to match them.`,
        };
      }
      if (!blocker) {
        return {
          id: variant.id,
          label: variant.aspectRatio,
          status: 'pass' as const,
          detail: `${variant.aspectRatio} has its image and has been reviewed.`,
        };
      }
      return {
        id: variant.id,
        label: variant.aspectRatio,
        status: blocker.reason === 'generating' ? ('skipped' as const) : ('fail' as const),
        detail: blocker.message,
      };
    });

  return {
    checks,
    passCount: checks.filter((c) => c.status === 'pass').length,
    warnCount: checks.filter((c) => c.status === 'warn').length,
    failCount: checks.filter((c) => c.status === 'fail').length,
  };
}
